import React, { useState, useEffect } from "react";
import { checkNoSpecialChars } from "./system-icons";
import getSvgImgSrc from "../../core/utils/getSvgImgSrc";
import CompanySelector from "./company-selector";
import isEqual from "lodash.isequal";
import UnsavedChanges, { UnsavedChangesManual } from "../../partials/unsaved-changes";

function CompanyLocationTypes(props) {
  const {
    companyList,
    companyLocationTypeLists,
    newCompanyLocationTypeToggle,
    editingCompanyLocationType,
    creatingNewCompanyLocationType,
    onChangeLocationTypeCompany,
    assignedCompanies,
    setCompanyLocationTypeName,
    setCompanyLocationTypeColor,
    setCompanyLocationTypeDefaultRadius,
    newLocationTypeCompany,
    onChangeCompanyLocationTypeIcon,
    svgIconList,
    companyIconLists,
    saveNewCompanyLocationType,
    svgIconListLoaded,
    companyLocationTypeToEdit,
    setCompanyLocationTypeToEditName,
    setCompanyLocationTypeToEditColor,
    onChangeCompanyLocationTypeToEditIcon,
    updateCompanyLocationTypeToEdit,
    cancel,
    backBtn,
    deleteLocationType,
    editLocationType,
    onSelectCompany = () => {},
  } = props;

  const [createNewDetails, setCreateNewDetails] = useState({
    company: "",
    name: "",
    color: "#929292",
    radius: 3,
    icon: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    color: "",
    radius: "",
  });
  const [disableCreateNew, setDisableCreateNew] = useState(true);
  const [presetSystemIcon, setPresetSystemIcon] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showManualModel, setShowManualModel] = useState(false);
  const [disableUpdateLocationType, setDisableUpdateLocationType] = useState(false);
  const [originalLocationTypeToEdit, setOriginalLocationTypeToEdit] = useState();
  const [radiusHasChanges, setRadiusHasChanges] = useState(false);

  const onCreateNewCompany = (company) => {
    onChangeLocationTypeCompany(company);
    setCreateNewDetails({ ...createNewDetails, company: company.target.value });
  };

  const onCreateNewName = (name, action = "create") => {
    switch (action) {
      case "edit":
        if (checkNoSpecialChars(name.target.value)) {
          setErrors({ ...errors, name: "" });
          setCompanyLocationTypeToEditName(name);
        } else {
          setErrors({
            ...errors,
            name: "*Special characters are not allowed.",
          });
        }
        break;

      default: // defaults to create new
        if (checkNoSpecialChars(name.target.value)) {
          setErrors({ ...errors, name: "" });
          setCreateNewDetails({ ...createNewDetails, name: name.target.value });
          setCompanyLocationTypeName(name);
        } else {
          setErrors({
            ...errors,
            name: "*Special characters are not allowed.",
          });
        }
        break;
    }
  };

  const onCreateNewColor = (color, action = "create") => {
    switch (action) {
      case "edit":
        setCompanyLocationTypeToEditColor(color);
        break;

      default: // defaults to create new
        setCreateNewDetails({ ...createNewDetails, color: color.target.value });
        setCompanyLocationTypeColor(color);
        break;
    }
  };

  const onCreateNewIcon = (icon, action = "create") => {
    switch (action) {
      case "edit":
        onSelectIcon(icon.target.value, "edit");
        onChangeCompanyLocationTypeToEditIcon(icon);
        break;

      default: // defaults to create new
        onSelectIcon(icon.target.value);
        onChangeCompanyLocationTypeIcon(icon);
        break;
    }
  };

  const onSelectIcon = (i, action = "create") => {
    let iconSvg = "";
    switch (action) {
      case "edit":
        let editAllIcons = [
          ...svgIconList,
          ...companyIconLists[companyLocationTypeToEdit.company_id.toString()],
        ];
        editAllIcons.forEach((e) => {
          if (e.id === Number(i)) {
            iconSvg = e.svg;
            setPresetSystemIcon(e.id);
          }
        });
        break;

      default: // defaults to create new
        let createAllIcons = [
          ...svgIconList,
          ...companyIconLists[newLocationTypeCompany.id.toString()],
        ];
        createAllIcons.forEach((e) => {
          if (e.id === Number(i)) {
            iconSvg = e.svg;
            setPresetSystemIcon(e.id);
          }
        });
        break;
    }
    setCreateNewDetails({ ...createNewDetails, icon: iconSvg });
  };

  const handleCancel = () => {
    setCreateNewDetails({
      name: "",
      color: "#929292",
      radius: 3,
      icon: "",
      company: "",
    });
    setErrors({
      name: "",
      color: "",
      radius: "",
    });
    setHasUnsavedChanges(false);
    cancel();
  };

  const onSubmitCreateNew = () => {
    saveNewCompanyLocationType();
    handleCancel();
  };

  const onBack = () => {
    handleCancel();
    backBtn();
  };

  useEffect(() => {
    const handleDisableCreateNew = () => {
      if (
        createNewDetails.name === "" ||
        createNewDetails.icon === "" ||
        createNewDetails.company === ""
      ) {
        setDisableCreateNew(true);
      } else {
        setDisableCreateNew(false);
      }
    };

    handleDisableCreateNew();
  }, [createNewDetails.name, createNewDetails.icon, createNewDetails.company]);

  useEffect(() => {
    setCompanyLocationTypeColor({ target: { value: "#929292" } });
    setCompanyLocationTypeDefaultRadius({ target: { value: "3" } });
    if (companyLocationTypeToEdit.svg_icon_id && svgIconList) {
      onSelectIcon(companyLocationTypeToEdit.svg_icon_id, "edit");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyLocationTypeToEdit, svgIconList]);

  /**
   * scroll to show editor section when clicking an item down the left side list
   */
  const scrollToEditor = React.useRef(null);
  useEffect(() => {
    let didCancel = false;
    if (!didCancel) {
      if (
        Object.keys(companyLocationTypeToEdit).length > 0 &&
        editingCompanyLocationType
      ) {
        scrollToEditor.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
    return () => {
      didCancel = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyLocationTypeToEdit]);

  const onClickCreateNew = () => {
    handleCancel();
    newCompanyLocationTypeToggle();
  };

  /**
   * Editing Functions
   */

  const onSubmitUpdate = () => {
    updateCompanyLocationTypeToEdit();
    handleCancel();
  };

  const checkBeforeLeave = () => {
    if(hasUnsavedChanges) {
      setShowManualModel(true);
    } else {
      onBack();
    }
  }

  const handleUnsavedLeave = () => {
    setShowManualModel(false);
    onBack();
  }

  const handleUnsavedCancel = () => {
    setShowManualModel(false);
  }

  const handleRadiusChanges = (_hasChanged) => {
    setRadiusHasChanges(!_hasChanged);
  }

  useEffect(() => {
    if (!disableCreateNew || !disableUpdateLocationType || radiusHasChanges) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [disableUpdateLocationType, disableCreateNew, radiusHasChanges])

  useEffect(() => {
    if(originalLocationTypeToEdit) {
      // the html selector saves it as string, api sends a number, make both string to compare
      let _original = {...originalLocationTypeToEdit, svg_icon_id: originalLocationTypeToEdit.svg_icon_id.toString()};
      let _new_changes = {...companyLocationTypeToEdit, svg_icon_id: companyLocationTypeToEdit.svg_icon_id.toString()};
      let hasChanges = isEqual(_original, _new_changes);
      setDisableUpdateLocationType(hasChanges);
    } else {
      setDisableUpdateLocationType(true);
    }
  // eslint-disable-next-line
  }, [companyLocationTypeToEdit, originalLocationTypeToEdit])

  useEffect(() => {
    if(Object.keys(companyLocationTypeToEdit).length) {
      setOriginalLocationTypeToEdit({...companyLocationTypeToEdit});
    }
  // eslint-disable-next-line
  }, [companyLocationTypeToEdit.id])

  return (
    <>
      <div className="flex-inline mb-4">
        <button type="button" className="btn-back-icons" onClick={checkBeforeLeave}>
          <i className="bi bi-chevron-left"></i> Back
        </button>
        <h3>Company Location Types</h3>
      </div>
      <div className="icon-list-section">
        <div className="left-section-wrapper">
          <CompanySelector
            list={companyList}
            locationType={companyLocationTypeLists}
            deleteLocationType={deleteLocationType}
            editLocationType={editLocationType}
            hasChanged={handleRadiusChanges}
            onCompanySelect={onSelectCompany}
          />
        </div>

        <div className="right-panel">
          {!editingCompanyLocationType && (
            <button
              onClick={onClickCreateNew}
              disabled={creatingNewCompanyLocationType}
            >
              Create New Company Location Type
            </button>
          )}

          {creatingNewCompanyLocationType && !editingCompanyLocationType && (
            <div className="create-new-section new-system-icon-sm">
              <br />
              <div>
                <span className="label">Company:</span>
                <select
                  onChange={onCreateNewCompany}
                  className="select-company"
                >
                  <option value="">Select a Company</option>
                  {assignedCompanies.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <span className="label">Name:</span>
                <input
                  type="text"
                  value={createNewDetails.name}
                  onChange={(e) => onCreateNewName(e)}
                />
                <span className="error-msg">{errors.name}</span>
              </div>

              <div className="color-picker">
                <span className="label">Color:</span>
                <input
                  type="color"
                  value={createNewDetails.color}
                  onChange={(e) => onCreateNewColor(e)}
                />
                <span className="display-hex-color">
                  {createNewDetails.color}
                </span>
                <span className="error-msg">{errors.color}</span>
              </div>

              {newLocationTypeCompany &&
                newLocationTypeCompany.id !== undefined &&
                createNewDetails.company !== "" && (
                  <div>
                    <span className="label">Icon:</span>
                    <select
                      onChange={(e) => onCreateNewIcon(e)}
                      className="select-icon"
                    >
                      <option />
                      {svgIconList.map((c) => (
                        <option value={c.id} key={c.id}>
                          {c.name}
                        </option>
                      ))}
                      {(companyIconLists[
                        newLocationTypeCompany.id.toString()
                      ] || []).map((c) => (
                        <option value={c.id} key={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <span>
                      {createNewDetails.icon.length > 5 && (
                        <img
                          height="25"
                          width="25"
                          src={getSvgImgSrc(createNewDetails.icon)}
                          alt="icon"
                        />
                      )}
                    </span>
                  </div>
                )}

              <div className="edit-btn-section">
                <button onClick={handleCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitCreateNew} disabled={disableCreateNew}>
                  Save
                </button>
              </div>
            </div>
          )}

          {editingCompanyLocationType &&
            svgIconListLoaded && (
              <div className="create-new-section new-system-icon-sm">
                <div className="scroll-stopper" ref={scrollToEditor}></div>
                <br />
                <div>
                  <span className="label">Edit Name:</span>
                  <input
                    type="text"
                    value={companyLocationTypeToEdit.name}
                    onChange={(e) => onCreateNewName(e, "edit")}
                  />
                  <span className="error-msg">{errors.name}</span>
                </div>

                <div className="color-picker">
                  <span className="label">Edit Color:</span>
                  <input
                    type="color"
                    value={companyLocationTypeToEdit.color}
                    onChange={(e) => onCreateNewColor(e, "edit")}
                  />
                  <span className="display-hex-color">
                    {companyLocationTypeToEdit.color}
                  </span>
                  <span className="error-msg">{errors.color}</span>
                </div>

                <div>
                  <span className="label">System Icon:</span>
                  <select
                    onChange={(e) => onCreateNewIcon(e, "edit")}
                    value={presetSystemIcon}
                    className="select-icon"
                  >
                    {[
                      ...svgIconList,
                      ...(companyIconLists[
                        companyLocationTypeToEdit.company_id.toString()
                      ] || []),
                    ].map((c) => (
                      <option value={c.id} key={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span>
                    {createNewDetails.icon.length > 5 && (
                      <img
                        height="25"
                        width="25"
                        src={getSvgImgSrc(createNewDetails.icon)}
                        alt="icon"
                      />
                    )}
                  </span>
                </div>

                <div className="edit-btn-section">
                  <button onClick={handleCancel}>Cancel</button>
                  <button className="promote" onClick={onSubmitUpdate} disabled={disableUpdateLocationType}>Update</button>
                </div>
              </div>
            )}
        </div>
      </div>
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
      <UnsavedChangesManual showModal={showManualModel} confirmLeave={handleUnsavedLeave} cancelLeave={handleUnsavedCancel} />
    </>
  );
}

export default CompanyLocationTypes;
